import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTitle } from 'react-use';

import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { useBrainId } from '@/hooks/useBrainId';
import useBrains from '@/hooks/useBrains';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';
import { scrollToElementById } from '@/util/util';

import {
  BasicSettingsFieldset,
  ModelFieldset,
  LanguagesFieldset,
  LegacyLanguagesFieldset,
  PreferencesFieldset,
  AdvancedSettingsFieldset,
  DeleteAgentFieldset,
} from '../components/fieldsets';

export const Settings = () => {
  useSegments(PageName.AI_AGENT_SETTINGS);
  const { t } = useTranslation();
  const { hash } = useLocation();
  const brainId = useBrainId();
  const { brain } = useBrains(brainId);
  const { speech_strategy } = useSelector(selectFeatureFlags);

  useDirtyFormBlocker();
  useTitle(t('pages.ai_agent_settings', { 0: brain?.name ?? '-' }));

  // Scroll to section based on URL hash once the agent has loaded, not on every refetch
  const isLoaded = !!brain;
  useEffect(() => {
    if (hash && isLoaded) {
      // Small delay to ensure the page is fully rendered
      const timeout = setTimeout(() => {
        scrollToElementById(hash.slice(1), { block: 'start' });
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [hash, isLoaded]);

  if (!brain || !brainId) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="flex w-full flex-col gap-8 pb-20">
        {/* Card 1: Basic Settings */}
        <SectionLayout title={t('ai_agents.settings.title')}>
          <BasicSettingsFieldset brain={brain} />
        </SectionLayout>

        {/* Card 2: Model Strategy */}
        <ModelFieldset brain={brain} />

        {/* Card 3: Languages */}
        {speech_strategy ? (
          <LanguagesFieldset brain={brain} />
        ) : (
          <LegacyLanguagesFieldset brain={brain} />
        )}

        {/* Card 4: Preferences (starting dialog + reminder messages) */}
        <PreferencesFieldset brain={brain} brainId={brainId} />

        {/* Card 5: Advanced Settings (confidence + factuality + timeout) */}
        <AdvancedSettingsFieldset brain={brain} />

        {/* Card 6: Delete AI Agent */}
        <DeleteAgentFieldset brain={brain} brainId={brainId} />
      </div>
    </div>
  );
};
